// The Settings sections, in nav order, and how a URL hash picks one.
//
// Every section is addressed by its hash (/settings#whatsapp). The keys
// the old seven-tab page used keep working through LEGACY_ALIASES
// (#agents held the model provider, market data and family agents, so it
// now opens Models; its other two cards moved to General), and a bare
// /settings opens General, the first section in the nav. A section with a `flag`
// (Tasks, Machines) is listed only where that workspace switch is on.

import type { LucideIcon } from "lucide-react";
import { AudioLines, Brain, ClipboardList, Clapperboard, FileText, Globe, KeyRound, Mail, MessageCircle, Palette, PenLine, Plug, Send, Server, SlidersHorizontal, Smartphone } from "lucide-react";

import type { CustomerSettingsFlag } from "@/api/customerSettings";

export type SectionKey =
  | "general"
  | "tasks"
  | "outside"
  | "appearance"
  | "devices"
  | "password"
  | "mail"
  | "telegram"
  | "whatsapp"
  | "models"
  | "connections"
  | "integrations"
  | "machines"
  | "styles"
  | "voices"
  | "media"
  | "signatures";

export type SectionGroup = "workspace" | "channels" | "agents" | "personal";

export interface SectionMeta {
  key: SectionKey;
  label: string;
  group: SectionGroup;
  icon: LucideIcon;
  /** One short clause under the section heading, or none. */
  description?: string;
  /** Listed only while this workspace switch is on. */
  flag?: CustomerSettingsFlag;
  /** Listed only while this workspace switch is NOT on (a section another one replaces). */
  offFlag?: CustomerSettingsFlag;
  /** Listed while ANY of these switches is on (Outside access: Apps, Deployments or Scheduling pages). */
  anyFlag?: readonly CustomerSettingsFlag[];
}

// Not "Workspace" / "Agents": the main sidebar already has a Workspace
// group and an Agents page, which mean something else.
export const GROUP_LABELS: Record<SectionGroup, string> = {
  workspace: "This workspace",
  channels: "Channels",
  agents: "What agents use",
  personal: "Just you",
};

/** The switches any one of which shows Settings › Outside access. */
export const OUTSIDE_FLAGS: readonly CustomerSettingsFlag[] = ["apps_enabled", "deployments_enabled", "booking_pages_enabled"];

/** A section's switches allow it: its flag on, its offFlag not on, and one of its anyFlag on. `=== true` throughout. */
export function sectionAllowed(
  s: Pick<SectionMeta, "flag" | "offFlag" | "anyFlag">,
  flags: Partial<Record<CustomerSettingsFlag, boolean>> | undefined,
): boolean {
  return (
    (!s.flag || flags?.[s.flag] === true) &&
    (!s.offFlag || flags?.[s.offFlag] !== true) &&
    (!s.anyFlag || s.anyFlag.some((f) => flags?.[f] === true))
  );
}

export const SECTIONS: readonly SectionMeta[] = [
  {
    key: "general",
    label: "General",
    group: "workspace",
    icon: SlidersHorizontal,
  },
  {
    key: "tasks",
    label: "Tasks",
    group: "workspace",
    icon: ClipboardList,
    flag: "tasks_enabled",
  },
  {
    // Everything people outside Lattice can open or call (apps, an app's
    // links and keys, deployment keys, scheduling links, older apps), with
    // Take down: one register over each owner's store (apps spec §10.4).
    key: "outside",
    label: "Outside access",
    group: "workspace",
    icon: Globe,
    description: "Everything people outside Lattice can open or call.",
    anyFlag: OUTSIDE_FLAGS,
  },
  {
    key: "mail",
    label: "Mail",
    group: "channels",
    icon: Mail,
    description: "Who agents may email.",
  },
  {
    key: "telegram",
    label: "Telegram",
    group: "channels",
    icon: Send,
  },
  {
    key: "whatsapp",
    label: "WhatsApp",
    group: "channels",
    icon: MessageCircle,
    description: "One number per agent.",
  },
  {
    key: "models",
    label: "Models",
    group: "agents",
    icon: Brain,
    description: "Used by every agent.",
  },
  {
    // Everything agents reach outside Lattice (GitLab, Motion, any MCP
    // server or keyed API, the data sources), in Integrations' place where
    // the workspace is on Connections. No line under the title: the page
    // is its title, Add and the list.
    key: "connections",
    label: "Connections",
    group: "agents",
    icon: Plug,
    flag: "connections_enabled",
  },
  {
    key: "integrations",
    label: "Integrations",
    group: "agents",
    icon: Plug,
    description: "Tools and APIs agents can use.",
    offFlag: "connections_enabled",
  },
  {
    // Computers agents run commands on (paired, or over SSH). Listed only
    // where machines are switched on for the workspace.
    key: "machines",
    label: "Machines",
    group: "agents",
    icon: Server,
    flag: "machines_enabled",
  },
  {
    key: "styles",
    label: "Style guides",
    group: "agents",
    icon: FileText,
    description: "Teach agents how your documents look.",
  },
  {
    key: "voices",
    label: "Voices",
    group: "agents",
    icon: AudioLines,
  },
  {
    // Not part of Voices (a library of voice clones, with its own iPhone
    // screen) nor Integrations (MCP servers and API keys agents add and
    // scope themselves): an operator-only sign-in that agents never see
    // (Lattice runs the signed-in fetch for them, services/youtube_signin.py).
    key: "media",
    label: "Media",
    group: "agents",
    icon: Clapperboard,
  },
  {
    key: "signatures",
    label: "Signatures",
    group: "agents",
    icon: PenLine,
    description: "Used only when you ask an agent to sign.",
  },
  {
    key: "appearance",
    label: "Appearance",
    group: "personal",
    icon: Palette,
    description: "Follows you on every device you sign in on.",
  },
  {
    // Your own sign-ins, not the workspace's: a phone belongs to the person
    // who signed in on it, so it sits under Just you.
    key: "devices",
    label: "Devices",
    group: "personal",
    icon: Smartphone,
  },
  {
    // Your own sign-in's password (routes/auth_password.py).
    key: "password",
    label: "Password",
    group: "personal",
    icon: KeyRound,
  },
] as const;

/** Old tab keys and likely spellings → the section that now holds them. */
export const LEGACY_ALIASES: Readonly<Record<string, SectionKey>> = {
  agents: "models",
  inference: "models",
  model: "models",
  family: "general",
  "market-data": "general",
  // the bell's "N things shared with you": General's Sharing card
  sharing: "general",
  "style-guides": "styles",
  "style-guide": "styles",
  mcp: "integrations",
  theme: "appearance",
  device: "devices",
  phones: "devices",
  security: "password",
  voice: "voices",
  youtube: "media",
  signature: "signatures",
  task: "tasks",
  machine: "machines",
  ssh: "machines",
  "outside-access": "outside",
  reachable: "outside",
};

export const DEFAULT_SECTION: SectionKey = "general";

/** The sections the nav lists: the ones these flags allow, and always the
 *  one on screen (a typed #tasks, or flags still loading). `=== true`, so a
 *  gated section never flashes in before the settings answer. */
export function visibleSections(flags: Partial<Record<CustomerSettingsFlag, boolean>> | undefined, active: SectionKey): SectionMeta[] {
  return SECTIONS.filter((s) => s.key === active || sectionAllowed(s, flags));
}

const BY_KEY = new Map(SECTIONS.map((s) => [s.key, s]));

export function sectionMeta(key: SectionKey): SectionMeta {
  return BY_KEY.get(key) ?? SECTIONS[0];
}

/**
 * The section a hash names. `canonical` is false when the hash is an
 * alias (or unknown), so the caller can rewrite the URL to the real key.
 */
export function resolveSection(hash: string): { key: SectionKey; canonical: boolean } {
  const raw = hash.replace(/^#/, "").trim().toLowerCase();
  if (!raw) return { key: DEFAULT_SECTION, canonical: true };
  if (BY_KEY.has(raw as SectionKey)) return { key: raw as SectionKey, canonical: true };
  return { key: LEGACY_ALIASES[raw] ?? DEFAULT_SECTION, canonical: false };
}
